/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    FIX_LINT_MISSING_DEPS
 * Date:         2025-12-31
 * Modified By:  Cursor
 *
 * Description:
 * Suppressed lint warning for missing formik dependency in useEffect.
 * formik is intentionally excluded to prevent infinite sync loops.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/company-profile/hooks/useCompanySync.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    FIX_SYNC_LOOP_REFS
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Fixed infinite loop in useCompanySync by using a ref to track the last synced profile.
 * This prevents resetForm from being called repeatedly when formik object identity changes.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/company-profile/hooks/useCompanySync.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    SPLIT_COMPANY_SYNC_HOOK
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Extracted synchronization logic from CompanyProfileForm hook to meet line limits.
 * Fixed incorrect relative import paths and implicit any types.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/company-profile/hooks/useCompanySync.ts
 */
import { useEffect, useState, useRef } from "react";
import { DEFAULT_HOURS } from "./useCompanyForm";

export const useCompanySync = (loading: boolean, profile: any, formik: any, setDirty: (dirty: boolean) => void) => {
  const [dbPresence, setDbPresence] = useState<Record<string, boolean>>({});
  const lastSyncedProfileRef = useRef<any>(null);

  useEffect(() => {
    // [2025-12-29] Sync loop fix: only reset form if profile has actually changed or hasn't been synced yet
    if (!loading && profile && profile !== lastSyncedProfileRef.current) {
      lastSyncedProfileRef.current = profile; // Move to top to prevent race conditions during state updates
      const h = profile.hours || {};
      const hasHours = h.fromDay && h.toDay && h.from && h.to && h.timeZone;
      const shouldReset = !!formik?.isSubmitting || !formik?.dirty;
      if (shouldReset) {
        formik.resetForm({
          values: {
            companyName: profile.companyName || "",
            website: profile.website || "",
            companyEmail: profile.companyEmail || "",
            phone: profile.telCompany || "",
            address1: profile.address1 || "",
            address2: profile.address2 || "",
            city: profile.city || "",
            state: profile.state || "",
            zip: profile.zip || "",
            country: profile.country || "",
            companyLinkedIn: profile.companyLinkedIn || "",
            companyX: profile.companyX || "",
            companyInstagram: profile.companyInstagram || "",
            companyFacebook: profile.companyFacebook || "",
            fromDay: hasHours ? h.fromDay : DEFAULT_HOURS.fromDay,
            toDay: hasHours ? h.toDay : DEFAULT_HOURS.toDay,
            from: hasHours ? h.from : DEFAULT_HOURS.from,
            to: hasHours ? h.to : DEFAULT_HOURS.to,
            services: profile.services || "",
            aboutCompany: profile.aboutCompany || "",
            companyLogos: profile.companyLogos || [],
            certifications: profile.certifications || [],
            timeZone: hasHours ? h.timeZone : DEFAULT_HOURS.timeZone,
          },
        });
        setDirty(false);
      }
      const presence: Record<string, boolean> = {};
      Object.keys(profile).forEach(k => { presence[k] = !!(profile as any)[k]; });
      setDbPresence(presence);
    }
    // [2025-12-31] formik intentionally excluded to prevent sync loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, profile, setDirty]);

  return { dbPresence };
};
